import { useCallback, useEffect, useState } from "react";
import {
  Filter,
  CommentContent,
  SimpleTagStructure,
} from "data/@types/GlobalSearchData";
import { useAdvancedViewFiltersStore } from "data/store/global-search/AdvancedViewFiltersStore";
import { globalSearchList } from "data/services/api/globalSearch";
import { useGlobalSearchStore } from "data/store/global-search/GlobalSearch";
import { useStableFilterCriteria } from "utils/global-search/useStableFilterCriteria";
import {
  formatQuarter,
  QUARTER_FORMAT_REGEX,
  QUARTER_PERIOD_OPTIONS,
  YEAR_OPTIONS,
} from "utils/quarter";

export type CommentTableRow = CommentContent & {
  entityName: string;
  commentLink: string;
  createdByPersonName: string;
  updatedByPersonName: string;
  scheduledById: number;
  createdById: number;
  updatedById: number;
  entityId?: number | string;
  entityLegacyId?: number | null;
  fundName: string;
  performanceSection: string;
  fundraisingSection: string;
  legalSection: string;
  restrictedText: string;
  isAnnualMeetingNote: boolean;
  esg: boolean;
  dei: boolean;
  netZero: boolean;
  quarterlyMonitoring: boolean;
  annualMonitoring: boolean;
  sec: boolean;
  annualMeetingCompliance: boolean;
  govComplete: boolean;
  oddComment: boolean;
  quarter: string | null;
  quarterYear: string | null;
  quarterPeriod: string | null;
};

export const commentFilters: Filter[] = [
  {
    label: "Comment ID",
    type: "text",
    field: "id",
  },
  {
    label: "Entity Legacy ID",
    type: "text",
    field: "entityLegacyId",
  },
  {
    label: "Entity RCU ID",
    type: "text",
    field: "entityId",
  },
  {
    label: "Author ID",
    type: "text",
    field: "scheduledById",
  },
  {
    label: "Comment Link",
    type: "text",
    field: "commentLink",
  },
  {
    label: "Entity Name",
    type: "text",
    field: "entityName",
  },
  {
    label: "Entity Type",
    type: "simple-select",
    options: ["Product", "Strategy", "Firm"],
    field: "entityType",
  },
  {
    label: "Universe",
    type: "checkbox-options",
    options: ["Public", "Private"],
    required: true,
    field: "universe",
  },
  {
    label: "Author",
    type: "text",
    field: "authorName",
  },
  {
    label: "Annual Meeting Note",
    type: "boolean",
    field: "isAnnualMeetingNote",
  },
  {
    label: "ESG",
    type: "boolean",
    field: "esg",
  },
  {
    label: "DEI",
    type: "boolean",
    field: "dei",
  },
  {
    label: "Net Zero",
    type: "boolean",
    field: "netZero",
  },
  {
    label: "Quarterly Monitoring",
    type: "boolean",
    field: "quarterlyMonitoring",
  },
  {
    label: "Quarter Year",
    type: "simple-select",
    options: YEAR_OPTIONS,
    field: "quarterYear",
    rowField: "quarterYear",
  },
  {
    label: "Quarter Period",
    type: "simple-select",
    options: QUARTER_PERIOD_OPTIONS,
    field: "quarterPeriod",
    rowField: "quarterPeriod",
  },
  {
    label: "Annual Monitoring",
    type: "boolean",
    field: "annualMonitoring",
  },
  {
    label: "SEC/Form ADV",
    type: "boolean",
    field: "sec",
  },
  {
    label: "Annual Meeting with Compliance",
    type: "boolean",
    field: "annualMeetingCompliance",
  },
  {
    label: "Gov Complete",
    type: "boolean",
    field: "govComplete",
  },
  {
    label: "ODD Comment",
    type: "boolean",
    field: "oddComment",
  },
  {
    label: "Restricted",
    type: "text",
    field: "restrictedText",
  },
  {
    label: "CAi Summary - Internal",
    type: "text",
    field: "aiSummaryInternal",
  },
  {
    label: "CAi Summary - External",
    type: "text",
    field: "aiSummaryExternal",
  },
  {
    label: "AAIM Fund",
    type: "text",
    field: "fundName",
  },
  {
    label: "Performance",
    type: "text",
    field: "performanceSection",
  },
  {
    label: "Fundraising",
    type: "text",
    field: "fundraisingSection",
  },
  {
    label: "Legal",
    type: "text",
    field: "legalSection",
  },
  {
    label: "Created By",
    type: "text",
    field: "createdByPersonName",
  },
  {
    label: "Created Date",
    type: "daterange",
    field: "creTimest",
  },
  {
    label: "Last Modified By",
    type: "text",
    field: "updatedByPersonName",
  },
  {
    label: "Last Modified Date",
    type: "daterange",
    field: "modTimest",
  },
  { field: "createdById", label: "Created By ID", type: "text" },
  { field: "updatedById", label: "Last Modified By ID", type: "text" },
];

function getTagFlags(tags?: SimpleTagStructure[]) {
  return {
    esg: !!tags?.find((t) => t.text.includes("ESG")),
    dei: !!tags?.find((t) => t.text.includes("DEI")),
    netZero: !!tags?.find((t) => t.text.includes("Net Zero")),
    quarterlyMonitoring: !!tags?.find((t) =>
      t.text.includes("Quarterly Monitoring"),
    ),
    annualMonitoring: !!tags?.find((t) => t.text.includes("Annual Monitoring")),
    sec: !!tags?.find((t) => t.text.includes("SEC/Form ADV")),
    annualMeetingCompliance: !!tags?.find((t) =>
      t.text.includes("Annual Discussion with Compliance Officer"),
    ),
    govComplete: !!tags?.find((t) => t.text.includes("Gov Complete")),
    oddComment: !!tags?.find((t) => t.text.includes("ODD Comment")),
  };
}

function getSectionContent(sections?: { content: string; name: string }[]) {
  return {
    performanceSection:
      sections?.find((s) => s.name.includes("Performance"))?.content ?? "--",
    fundraisingSection:
      sections?.find((s) => s.name.includes("FundRaising"))?.content ?? "--",
    legalSection:
      sections?.find((s) => s.name.includes("Legal"))?.content ?? "--",
  };
}

const transformCommentRow = (
  comment: CommentContent,
  fundComment?: {
    fund?: {
      id?: number;
      uuid?: string;
      longName?: string;
      firmStrategyId?: number;
    };
    sections?: { content: string; name: string }[];
  },
): CommentTableRow => {
  const entityNameDisplay =
    comment.entityType === "Firm"
      ? comment?.firm?.longName
      : comment?.entity?.longName;

  const entityIdDisplay = comment?.entity?.id ?? comment.firm?.id;

  const firmLegacyId =
    comment?.universe === "Public"
      ? comment?.firm?.imisId
      : comment?.firm?.aadbId;

  const entityLegacyIdDisplay = comment?.entity?.legacyId ?? firmLegacyId;

  const { performanceSection, fundraisingSection, legalSection } =
    getSectionContent(fundComment?.sections);

  const tagFlags = getTagFlags(comment?.tags ?? undefined);

  const quarterMatch = comment?.quarter?.match(QUARTER_FORMAT_REGEX) ?? null;
  const quarterYear = quarterMatch ? quarterMatch[1] : null;
  const quarterPeriod = quarterMatch ? `Q${quarterMatch[2]}` : null;

  return {
    ...comment,
    entityName: entityNameDisplay ?? "",
    entityId: entityIdDisplay,
    entityLegacyId: entityLegacyIdDisplay,
    scheduledById: comment.authorPerson?.id,
    commentLink:
      comment.customTitle ||
      `${comment?.firm?.longName ?? ""} ` +
        `${comment?.entity?.longName ?? ""}`,
    createdByPersonName: comment.createdByPerson?.name,
    updatedByPersonName: comment.updatedByPerson?.name,
    createdById: comment?.createdByPerson?.id,
    updatedById: comment?.updatedByPerson?.id,
    fundName: fundComment?.fund?.longName || "--",
    restrictedText: comment?.sections?.InternalTextBETA || "--",
    isAnnualMeetingNote: !!comment.isAnnualMeetingNote,
    performanceSection,
    fundraisingSection,
    legalSection,
    quarter: formatQuarter(comment?.quarter),
    quarterYear,
    quarterPeriod,
    ...tagFlags,
  };
};

const splitCommentsByFunds = (
  comments: CommentContent[],
): CommentTableRow[] => {
  const result: CommentTableRow[] = [];

  comments.forEach((comment) => {
    if (
      Array.isArray(comment.fundComments) &&
      comment.fundComments.length > 0
    ) {
      comment.fundComments.forEach((fundComment) => {
        result.push(transformCommentRow(comment, fundComment));
      });
    } else {
      result.push(transformCommentRow(comment));
    }
  });

  return result;
};

export const useGlobalSearchCommentsTable = (initialRows: CommentContent[]) => {
  const { activeFilters, setCommentFilterCriteria } =
    useAdvancedViewFiltersStore((state) => ({
      activeFilters: state.filtersByActivity["Comments"] ?? {},
      setCommentFilterCriteria: state.setCommentFilterCriteria,
    }));
  const searchTerm = useGlobalSearchStore((state) => state.searchTerm);

  const [rows, setRows] = useState<CommentContent[]>(initialRows);
  const [isBackendLoading, setIsBackendLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const backendFilterCriteria = useStableFilterCriteria(
    commentFilters,
    activeFilters,
  );

  useEffect(() => {
    setCommentFilterCriteria(backendFilterCriteria);
    return () => setCommentFilterCriteria(undefined);
  }, [backendFilterCriteria, setCommentFilterCriteria]);

  useEffect(() => {
    if (searchTerm) {
      setRows(initialRows);
    }
  }, [initialRows, searchTerm]);

  useEffect(() => {
    if (searchTerm) return;

    let cancelled = false;

    const debounce = setTimeout(() => {
      setIsBackendLoading(true);
      setFetchError(false);

      globalSearchList("COMMENT", "desc", backendFilterCriteria)
        .then(({ data }) => {
          if (!cancelled) setRows(data?.comment?.content ?? []);
        })
        .catch((err) => {
          console.error(err);
          if (!cancelled) {
            setRows([]);
            setFetchError(true);
          }
        })
        .finally(() => {
          if (!cancelled) setIsBackendLoading(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [backendFilterCriteria, searchTerm]);

  const applyFilters = useCallback(
    (commentContent: CommentContent[]) => splitCommentsByFunds(commentContent),
    [],
  );

  return {
    applyFilters,
    commentFilters,
    rows,
    isBackendLoading,
    fetchError,
  };
};
